// ════════════════════════════════════════════════════════════
// Navigation — source UNIQUE du sitemap (ECR)
// ════════════════════════════════════════════════════════════
// Alimente Sidebar (desktop), SidebarDrawer (mobile) ET
// MobileBottomNav. La VISIBILITÉ par rôle n'est PAS encodée ici :
// chaque surface filtre via `canAccessPath(role, item.to)` de
// `@/types/auth` (source de vérité unique). On ne duplique pas
// la matrice d'accès.
//
// ECR = version light : Dépenses + Encaissements + Trésorerie.
// ════════════════════════════════════════════════════════════
import {
  Home,
  ArrowUpFromLine,
  ArrowDownToLine,
  Landmark,
  Users,
  Settings,
  type LucideIcon,
} from 'lucide-react';

export type NavGroup = 'Finance' | 'Administration';

/** Ordre d'affichage des groupes dans la sidebar. */
export const NAV_GROUP_ORDER: readonly NavGroup[] = ['Finance', 'Administration'] as const;

export interface NavItem {
  /** Libellé affiché (déjà en français — l'app est mono-langue). */
  label: string;
  /** Libellé court (bottom nav mobile, espace contraint). Défaut = label. */
  shortLabel?: string;
  icon: LucideIcon;
  /** Route TanStack Router RÉELLE. */
  to: string;
  /** Groupe de la sidebar. Absent = item « haut » non groupé (Accueil). */
  group?: NavGroup;
  /** Épinglé en bas de la sidebar/drawer (Mon compte). */
  footer?: boolean;
  /** Exposé dans la MobileBottomNav (barre fixe mobile). */
  bottomNav?: boolean;
  /** Badge optionnel (ex. « Bientôt »). */
  badge?: string;
}

// Ordre = ordre d'affichage. La visibilité réelle est dérivée du rôle.
export const NAV_ITEMS: readonly NavItem[] = [
  // ─── Haut (non groupés) ───
  { label: 'Accueil', icon: Home, to: '/', bottomNav: true },

  // ─── Finance ───
  {
    label: 'Dépenses',
    shortLabel: 'Dépenses',
    icon: ArrowUpFromLine,
    to: '/depenses',
    group: 'Finance',
    bottomNav: true,
  },
  {
    label: 'Encaissements',
    shortLabel: 'Encaiss.',
    icon: ArrowDownToLine,
    to: '/paiements',
    group: 'Finance',
    bottomNav: true,
  },
  {
    label: 'Trésorerie',
    shortLabel: 'Tréso.',
    icon: Landmark,
    to: '/tresorerie',
    group: 'Finance',
    bottomNav: true,
  },

  // ─── Administration (DAF only — filtré par canAccessPath) ───
  { label: 'Utilisateurs', icon: Users, to: '/admin', group: 'Administration' },

  // ─── Bas (épinglés) ───
  { label: 'Mon compte', icon: Settings, to: '/compte', footer: true },
] as const;

/**
 * Un item de nav est-il actif pour le pathname courant ?
 * Match exact pour `/` (sinon tout serait actif) ; sinon préfixe
 * (`/depenses/new` active « Dépenses »). Aligné sur le matching
 * TanStack Router (segments d'URL).
 */
export function isNavItemActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}
